import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { ymGoal } from "../components/Analytics";

const interestsList = [
  { key: "mountains", ru: "Горы", en: "Mountains" },
  { key: "nature", ru: "Природа", en: "Nature" },
  { key: "ethno", ru: "Этно", en: "Ethno" },
  { key: "history", ru: "История", en: "History" },
  { key: "photo", ru: "Фотография", en: "Photography" },
  { key: "wild", ru: "Дикие места", en: "Wild places" },
  { key: "easy", ru: "Лёгкие прогулки", en: "Easy walks" },
  { key: "active", ru: "Активные маршруты", en: "Active routes" },
];

export default function CustomTour() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ customer_name: "", phone: "", telegram: "", email: "", dates: "", people: 2, has_children: false, children_ages: "", days: "", interests: [], route_notes: "", transport: "", comment: "", consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k) => setForm((f) => ({ ...f, interests: f.interests.includes(k) ? f.interests.filter((x) => x !== k) : [...f.interests, k] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error(lang === "ru" ? "Подтвердите согласие" : "Please confirm consent"); return; }
    setLoading(true);
    try {
      await api.post("/custom-tours", { ...form, people: Number(form.people), days: form.days ? Number(form.days) : null, language: lang, consent_version: "2026-08-16" });
      toast.success(t("thanks"));
      ymGoal("custom_tour_submit");
      setForm({ customer_name: "", phone: "", telegram: "", email: "", dates: "", people: 2, has_children: false, children_ages: "", days: "", interests: [], route_notes: "", transport: "", comment: "", consent: false });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const field = "w-full bg-transparent border-b border-argali-brown/30 py-2.5 text-argali-obsidian placeholder-argali-brown/40 focus:border-argali-bronze outline-none transition-colors";

  return (
    <div data-testid="custom-tour-page" style={{ background: "#F4F0E7", minHeight: "100vh" }}>
      <Seo path="/custom-tour" lang={lang} title={lang === "ru" ? "Индивидуальный маршрут" : "Custom journey"} description={lang === "ru" ? "Соберём индивидуальное путешествие по Алтаю под ваши даты, интересы и ритм." : "We'll design a custom Altai journey for your dates, interests and rhythm."} />
      <div className="max-w-3xl mx-auto px-5 md:px-10 pt-32 pb-24">
        <Reveal>
          <p className="eyebrow" style={{ color: "#A96818" }}>{t("cta_custom")}</p>
          <h1 className="display display-lg text-argali-obsidian mt-3">{lang === "ru" ? "Соберём" : "Let's design"}<br /><span className="text-argali-bronze">{lang === "ru" ? "ваш Алтай." : "your Altai."}</span></h1>
          <p className="text-argali-brown/70 mt-5">{lang === "ru" ? "Расскажите, каким вы хотите его увидеть." : "Tell us how you want to see it."}</p>
        </Reveal>

        <form onSubmit={submit} className="mt-12 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <input required data-testid="ct-name" className={field} placeholder={t("form_name")} value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
            <input required data-testid="ct-phone" className={field} placeholder={t("form_phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            <input data-testid="ct-tg" className={field} placeholder="Telegram / WhatsApp" value={form.telegram} onChange={(e) => set("telegram", e.target.value)} />
            <input data-testid="ct-email" className={field} placeholder={t("form_email")} value={form.email} onChange={(e) => set("email", e.target.value)} />
            <input data-testid="ct-dates" className={field} placeholder={t("form_date")} value={form.dates} onChange={(e) => set("dates", e.target.value)} />
            <input type="number" min="1" data-testid="ct-people" className={field} placeholder={lang === "ru" ? "Количество человек" : "Number of people"} value={form.people} onChange={(e) => set("people", e.target.value)} />
            <input type="number" min="1" data-testid="ct-days" className={field} placeholder={lang === "ru" ? "Количество дней" : "Number of days"} value={form.days} onChange={(e) => set("days", e.target.value)} />
            <input data-testid="ct-children" className={field} placeholder={lang === "ru" ? "Дети и их возраст" : "Children and ages"} value={form.children_ages} onChange={(e) => set("children_ages", e.target.value)} />
          </div>

          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.16em] text-argali-brown/50 mb-3">{lang === "ru" ? "Интересы" : "Interests"}</p>
            <div className="flex flex-wrap gap-2">
              {interestsList.map((it) => (
                <button type="button" key={it.key} data-testid={`ct-interest-${it.key}`} onClick={() => toggle(it.key)}
                  className="text-[0.68rem] uppercase tracking-[0.14em] px-3.5 py-2 border transition-colors"
                  style={{ borderColor: form.interests.includes(it.key) ? "#A96818" : "rgba(52,33,22,0.25)", background: form.interests.includes(it.key) ? "#A96818" : "transparent", color: form.interests.includes(it.key) ? "#FAFAF7" : "#342116" }}>
                  {lang === "ru" ? it.ru : it.en}
                </button>
              ))}
            </div>
          </div>

          <textarea rows={3} data-testid="ct-notes" className={field} placeholder={lang === "ru" ? "Маршрутные пожелания и комментарии" : "Route wishes and comments"} value={form.route_notes} onChange={(e) => set("route_notes", e.target.value)} />

          <label className="flex items-start gap-3 text-xs text-argali-brown/70 cursor-pointer">
            <input type="checkbox" data-testid="ct-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 accent-argali-bronze" />
            <span>
                {lang === "ru" ? "Я даю " : "I give "}
                <a href="/personal-data-consent" target="_blank" rel="noreferrer" className="underline text-argali-bronze" onClick={(e) => e.stopPropagation()}>{lang === "ru" ? "согласие на обработку персональных данных" : "consent to personal data processing"}</a>
                {lang === "ru" ? " и ознакомлен(а) с " : " and have read the "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-argali-bronze" onClick={(e) => e.stopPropagation()}>{lang === "ru" ? "Политикой" : "Policy"}</a>.
              </span>
          </label>

          <button type="submit" disabled={loading} data-testid="ct-submit" className="btn-bronze group">{loading ? "…" : t("cta_custom")} <ArrowRight size={16} className="btn-arrow" /></button>
        </form>
      </div>
    </div>
  );
}
